import { FlyUserInvitation, IdName, USER_ROLES, UserRole } from '@fly/shared';
import { apiSlice } from '@fly/store';

import { TeamMember } from '../types';

function getRole(roles: Array<IdName>): string {
  const role = roles.find((role) => USER_ROLES[role.name]);
  if (role) {
    const key = Object.entries(USER_ROLES).find(
      ([id, name]) => name === USER_ROLES[role.name]
    )?.[0];
    return key || UserRole.DEVELOPER;
  }
  return UserRole.DEVELOPER;
}

export const teamService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTeamMembers: builder.query<TeamMember[], void>({
      query: () => ({
        url: '/users/',
        method: 'GET',
      }),
      transformResponse: (response: TeamMember[]) =>
        response
          .map((member) => ({
            ...member,
            role: getRole(member.roles),
          }))
          .sort((a, b) => a.email.localeCompare(b.email)),
      providesTags: ['users'],
    }),

    inviteTeamMember: builder.mutation<TeamMember, FlyUserInvitation>({
      query: (invitationData) => ({
        url: '/users/invite',
        method: 'POST',
        data: invitationData,
      }),
      invalidatesTags: ['users'],
    }),

    deleteTeamMember: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['users'],
    }),

    updateTeamMemberRole: builder.mutation<
      { user_id: string },
      { id: string; role: string }
    >({
      query: ({ id, role }) => ({
        url: `/users/${id}`,
        method: 'PATCH',
        data: { role },
      }),
      onQueryStarted: async ({ id, role }, { queryFulfilled, dispatch }) => {
        const response = await queryFulfilled;

        if (!response.data?.user_id) {
          return;
        }

        dispatch(
          teamService.util.updateQueryData(
            'getTeamMembers',
            undefined,
            (draft) => {
              return draft.map((member) => {
                if (member.id === id) {
                  return { ...member, role };
                }
                return member;
              });
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetTeamMembersQuery,
  useInviteTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useUpdateTeamMemberRoleMutation,
} = teamService;
